import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { REPORT_TEMPLATE_STATUS } from '@lims/shared-types';
import { reportTemplatesApi } from '../../api/endpoints.js';
import {
  Alert,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import {
  ActedBy,
  LanguageList,
  TEMPLATES_QUERY_KEY,
  TemplateBadges,
  TemplateManageActions,
  TemplatePreviewPanel,
  templateName,
} from './report-template-parts.js';
import { ReportLayoutPanel, TemplateTestsPanel } from './ReportLayoutPanel.js';

/**
 * One report template: its facts, the tests that use it as their own (D254),
 * its section layout (D251) and its A4 preview (D255).
 *
 * The layout arranges sections and never defines a test's fields: each test's
 * results are printed by its own result template. A withdrawn template keeps
 * its page, because reports issued on it still name it; it offers no action.
 */
export function ReportTemplateDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const locale = currentLocale();

  const detail = useQuery({
    queryKey: [...TEMPLATES_QUERY_KEY, 'detail', id],
    queryFn: () => reportTemplatesApi.get(id),
    enabled: id !== '',
  });

  if (detail.isLoading) return <LoadingState />;
  if (detail.error || !detail.data) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  const template = detail.data;
  const name = templateName(template, locale);
  const isRetired = template.status === REPORT_TEMPLATE_STATUS.RETIRED;

  return (
    <>
      <PageHeader
        title={name}
        subtitle={t('reportTemplates.subtitle')}
        breadcrumbs={[
          { label: t('nav.reportTemplates'), to: '/report-templates' },
          { label: name },
        ]}
        actions={<TemplateManageActions template={template} size="md" />}
      />

      <div className="flex flex-col gap-gutter">
        {isRetired ? (
          <Alert tone="warning" title={t('reportTemplates.retiredNotice')} />
        ) : null}

        <div className="grid gap-gutter lg:grid-cols-2">
          <Panel title={t('reportTemplates.facts')}>
            <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
              {/* Each name is shown in its own direction, exactly as entered. */}
              <ReadOnlyField label={t('reportTemplates.nameAr')}>
                {template.name.ar ? (
                  <bdi dir="rtl">{template.name.ar}</bdi>
                ) : (
                  t('common.notRecorded')
                )}
              </ReadOnlyField>
              <ReadOnlyField label={t('reportTemplates.nameEn')}>
                {template.name.en ? (
                  <bdi dir="ltr">{template.name.en}</bdi>
                ) : (
                  t('common.notRecorded')
                )}
              </ReadOnlyField>
              {/* Not a ReadOnlyField: a badge is a block, and cannot sit in its paragraph. */}
              <div className="min-w-0">
                <p className="text-caption text-default-600">{t('common.status')}</p>
                <div className="mt-1">
                  <TemplateBadges template={template} />
                </div>
              </div>
              <ReadOnlyField label={t('reportTemplates.columnDefault')}>
                {template.isDefault ? t('common.yes') : t('common.no')}
              </ReadOnlyField>
              <ReadOnlyField label={t('reportTemplates.languages')}>
                <LanguageList languages={template.languages} />
              </ReadOnlyField>
              <ReadOnlyField label={t('reportTemplates.revision')}>
                <span className="tabular-nums">{template.revision}</span>
              </ReadOnlyField>
            </div>
            <p className="mt-2 text-caption text-default-600">
              {t('reportTemplates.revisionHint')}
            </p>

            <div className="mt-gutter flex flex-col gap-inline">
              <ReadOnlyField label={t('reportTemplates.layout')}>
                {t(`reportTemplateLayout.${template.layout}`)}
              </ReadOnlyField>
              <p className="text-caption text-default-600">{t('reportTemplates.layoutHintHere')}</p>
            </div>

            <div className="mt-gutter flex flex-col gap-inline">
              <ReadOnlyField label={t('reportTemplates.issuedVersionCount')}>
                <span className="tabular-nums">{template.issuedVersionCount}</span>
              </ReadOnlyField>
              <p className="text-caption text-default-600">
                {t('reportTemplates.issuedVersionCountHint')}
              </p>
            </div>

            {/* Who did what, and when — the name and the date each isolated. */}
            <ul className="mt-gutter flex flex-col gap-1 border-t border-default-100 pt-3 text-caption text-default-700">
              <li>
                <ActedBy
                  sentenceKey="reportTemplates.createdBy"
                  person={template.createdBy}
                  at={template.createdAt}
                />
              </li>
              {template.updatedAt ? (
                <li>
                  <ActedBy
                    sentenceKey="reportTemplates.updatedBy"
                    person={template.updatedBy}
                    at={template.updatedAt}
                  />
                </li>
              ) : null}
              {template.retiredAt ? (
                <li>
                  <ActedBy
                    sentenceKey="reportTemplates.retiredBy"
                    person={template.retiredBy}
                    at={template.retiredAt}
                  />
                </li>
              ) : null}
            </ul>

            {isRetired ? (
              <div className="mt-stack">
                <ReadOnlyField label={t('reportTemplates.retireReasonLabel')}>
                  {/* The manager's own words, verbatim. */}
                  <bdi className="whitespace-pre-wrap">
                    {template.retireReason ?? t('common.notRecorded')}
                  </bdi>
                </ReadOnlyField>
              </div>
            ) : null}
          </Panel>

          <TemplateTestsPanel template={template} />
        </div>

        <ReportLayoutPanel template={template} />

        <div className="max-w-3xl">
          <TemplatePreviewPanel template={template} />
        </div>
      </div>
    </>
  );
}
